import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { indexFromAngle, restingAngle } from './wheel-geometry';
import styles from './aroma-wheel.module.scss';

/**
 * La roue des arômes.
 *
 * « Au doigt, la roue tourne jusqu'à caler la famille choisie sous le repère, avec
 * crantage · 320 ms · aimantation sur 60° · vibration au cran. » La planche d'animation
 * ne dit rien de plus, et c'est suffisant : six secteurs de soixante degrés, ce qui
 * correspond exactement aux six familles du contrat partagé.
 *
 * Elle vit dans `shared/` et non dans `features/aromas/` depuis qu'elle sert à deux
 * endroits : l'écran 14 la parcourt en lecture, l'écran 04 s'en sert pour choisir. Ranger
 * un composant partagé dans une feature aurait fait dépendre le sélecteur de note d'un
 * écran qui n'a rien à voir avec la saisie.
 *
 * **La roue est le chemin, elle doit donc être praticable sans doigt.** Un cercle qu'on
 * fait tourner au pointeur exclurait le clavier et les lecteurs d'écran : elle est
 * pilotable aux quatre flèches et annonce la famille sous le repère par `aria-valuetext`.
 * Les descripteurs, eux, restent des boutons ordinaires en dessous.
 *
 * Le sens de rotation demande une convention. Un `conic-gradient` part de midi et tourne
 * dans le sens horaire : le secteur `i` occupe donc `[i × 60°, (i + 1) × 60°[`, et son
 * centre est à `i × 60 + 30`. Pour amener ce centre sous le repère — fixe, à midi — il
 * faut tourner la roue de `−(i × 60 + 30)`. Toute l'arithmétique ci-dessous découle de là.
 */

export interface AromaWheelProps {
  /** Les six familles, dans l'ordre des secteurs. */
  families: readonly string[];
  index: number;
  onChange: (index: number) => void;
  /** Libellé secondaire affiché sous le nom de la famille, au centre. */
  caption?: string;
}



export function AromaWheel({ families, index, onChange, caption }: AromaWheelProps): ReactNode {
  const count = families.length;
  const [angle, setAngle] = useState(() => restingAngle(index));
  const [dragging, setDragging] = useState(false);
  const discRef = useRef<HTMLDivElement>(null);
  // La position du doigt au moment où l'on a saisi la roue, pour tourner par différence
  // plutôt que de faire sauter le secteur sous le point de contact.
  const grab = useRef<{ pointerAngle: number; startAngle: number } | null>(null);

  // La roue suit l'index quand il change ailleurs — au clavier, ou depuis la liste.
  useEffect(() => {
    if (!dragging) setAngle(restingAngle(index));
  }, [index, dragging]);

  const settle = useCallback(
    (raw: number) => {
      const next = indexFromAngle(raw, count);
      if (next !== index) {
        // Le cran se sent avant de se voir. Absent sur iOS, d'où le test.
        navigator.vibrate?.(8);
        onChange(next);
      }
      setAngle(restingAngle(next));
    },
    [count, index, onChange],
  );

  function pointerAngleOf(event: PointerEvent<HTMLDivElement>): number {
    const box = discRef.current?.getBoundingClientRect();
    if (!box) return 0;
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    // atan2 mesure depuis l'axe horizontal ; on ramène l'origine à midi.
    return (Math.atan2(event.clientY - cy, event.clientX - cx) * 180) / Math.PI + 90;
  }

  return (
    <div className={styles.stage}>
      <div className={styles.disc}>
        {/* Le repère doré, fixe : c'est la roue qui bouge sous lui. */}
        <span className={styles.pointer} aria-hidden="true" />

        <div
          ref={discRef}
          className={[styles.wheel, dragging && styles.wheelDragging].filter(Boolean).join(' ')}
          style={{ transform: `rotate(${angle}deg)` }}
          role="slider"
          tabIndex={0}
          aria-label="Roue des arômes"
          aria-valuemin={1}
          aria-valuemax={count}
          aria-valuenow={index + 1}
          aria-valuetext={families[index]}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            grab.current = { pointerAngle: pointerAngleOf(event), startAngle: angle };
            setDragging(true);
          }}
          onPointerMove={(event) => {
            if (!grab.current) return;
            const delta = pointerAngleOf(event) - grab.current.pointerAngle;
            setAngle(grab.current.startAngle + delta);
          }}
          onPointerUp={(event) => {
            if (!grab.current) return;
            const delta = pointerAngleOf(event) - grab.current.pointerAngle;
            const raw = grab.current.startAngle + delta;
            grab.current = null;
            setDragging(false);
            settle(raw);
          }}
          onPointerCancel={() => {
            grab.current = null;
            setDragging(false);
            setAngle(restingAngle(index));
          }}
          onKeyDown={(event) => {
            // Les deux axes : une roue se tourne à gauche et à droite, mais on la lit de
            // haut en bas. Les quatre flèches font donc la même chose, deux à deux.
            const step =
              event.key === 'ArrowRight' || event.key === 'ArrowDown'
                ? 1
                : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                  ? -1
                  : 0;
            if (step === 0) return;
            event.preventDefault();
            onChange((index + step + count) % count);
          }}
        >
          <div className={styles.hub} />
        </div>

        {/* Posé sur le moyeu, hors du disque : dedans, il tournerait avec lui. */}
        <strong className={styles.familyName} aria-hidden="true">
          {families[index]}
        </strong>
      </div>

      {/*
        La légende passe sous la roue et non dans le moyeu : « 0 choisi · 4 descripteurs »
        est deux fois plus large que le disque central, et au téléphone elle débordait sur
        les secteurs. Sous la roue, elle a toute la largeur de l'écran pour elle.
      */}
      {caption ? (
        <span className={styles.caption} aria-hidden="true">
          {caption}
        </span>
      ) : null}
    </div>
  );
}
